import React, { useEffect, useState } from "react";
import { db } from "../../config/firebaseConfig";
import { ref, get } from "firebase/database";
import { Skeleton } from "../../@/components/ui/skeleton";

interface AboutSection {
  title: string;
  content: string;
}

interface AboutData {
  intro: string;
  sections: AboutSection[];
}

const AboutSkeleton = () => (
  <div className="p-4 md:p-4">
    <div className="p-4 md:px-[15vw] md:pt-[5vh] w-full flex flex-col font-normal text-sm space-y-5">
      <div>
        <Skeleton className="mt-[4%] h-16 w-3/4" />
      </div>

      <Skeleton className="h-20 w-full" />

      <div className="space-y-6">
        {[1, 2, 3, 4, 5].map((index) => (
          <div key={index} className="space-y-2">
            <Skeleton className="h-6 w-1/3" />
            <Skeleton className="h-24 w-full" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

const About: React.FC = () => {
  const [aboutData, setAboutData] = useState<AboutData>({
    intro: "",
    sections: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Scroll to the top when component mounts
    const aboutElement = document.getElementById("topbartopscroll");
    if (aboutElement) {
      aboutElement.scrollIntoView({ behavior: "smooth" });
    }

    // Fetch about us data from Firebase
    const fetchAboutData = async () => {
      try {
        const aboutUsRef = ref(db, "LayoutSettingData/AboutUs");
        const snapshot = await get(aboutUsRef);

        if (snapshot.exists()) {
          const data = snapshot.val();

          // Handle legacy data format
          if (!data.sections) {
            data.sections = [
              { title: "Our Mission", content: data.ourMission || "" },
              { title: "Our Story", content: data.ourStory || "" },
              {
                title: "Why Choose Afrocart?",
                content: data.whyChooseUs || "",
              },
              { title: "Our Vision", content: data.ourVision || "" },
              { title: "Get Involved", content: data.getInvolved || "" },
            ];
          }

          setAboutData(data);
        }
      } catch (error) {
        console.error("Error fetching about us data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAboutData();
  }, []);

  if (loading) {
    return <AboutSkeleton />;
  }

  // Helper function to format content with email links
  const formatContent = (content: string) => {
    if (content.includes("admin@afrocart.ca")) {
      const parts = content.split("admin@afrocart.ca");
      return (
        <>
          {parts[0]}
          <a href="mailto:admin@afrocart.ca" className="text-customGreenColor">
            admin@afrocart.ca
          </a>
          {parts[1]}
        </>
      );
    }
    return content;
  };

  return (
    <div className="p-4 md:p-4">
      <div className="p-4 md:px-[15vw] md:pt-[5vh] w-full flex flex-col font-normal text-sm space-y-5 text-black">
        <div>
          {/* <p className="flex items-start text-[1.65vh] text-black">
            Last updated: 10/22/2024
          </p> */}
          <h1 className="mt-[4%] font-helveticaBold text-[5.5vh] xxl:text-[7.5vh] leading-none text-black">
            About Afrocart
          </h1>
        </div>

        <p className="font-helvetica">
          Welcome to <span className="font-helveticaBold">Afrocart</span>,
          {aboutData.intro.substring(aboutData.intro.indexOf(",") + 1)}
        </p>

        <ol className="decimal_ol space-y-4">
          {aboutData.sections.map((section, index) => (
            <li key={index} className="font-helveticaBold text-black">
              {section.title}
              <p className="font-helvetica text-black whitespace-pre-line">
                {formatContent(section.content)}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
};

export default About;
